import { Component } from '@theme/component';

/**
 * Handles text truncation with show more/less functionality
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [textContent] - The text content element to truncate
 * @property {HTMLElement} [toggleButton] - The show more/less toggle button
 * @property {HTMLElement} [showMoreText] - The "Show more" text element
 * @property {HTMLElement} [showLessText] - The "Show less" text element
 *
 * @extends {Component<Refs>}
 */
class TextShowMore extends Component {
  /**
   * @type {boolean}
   */
  #expanded = false;

  /**
   * @type {number}
   */
  #truncateLines = 3;

  /**
   * @type {number}
   */
  #collapsedHeight = 0;

  /**
   * @type {Animation | undefined}
   */
  #animation;

  /**
   * @constant {number}
   */
  #animationSpeed = 300;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  /**
   * Initialize the show more functionality
   */
  #initialize() {
    const container = this;
    const textContent = this.refs.textContent || container.querySelector('[data-text-content]');
    const toggleButton = this.refs.toggleButton || container.querySelector('[data-show-more-toggle]');
    const showMoreText = this.refs.showMoreText || container.querySelector('.text-block__show-more-text');
    const showLessText = this.refs.showLessText || container.querySelector('.text-block__show-less-text');

    if (!textContent || !toggleButton) return;

    this.#truncateLines = parseInt(container.dataset.truncateLines || '3', 10);
    container.style.setProperty('--truncate-lines', String(this.#truncateLines));

    const textContentElement = /** @type {HTMLElement} */ (textContent);
    this.#collapsedHeight = textContentElement.offsetHeight;

    toggleButton.addEventListener('click', this.#handleToggle.bind(this));

    this.#checkIfTruncationNeeded(textContentElement, /** @type {HTMLElement} */ (toggleButton));

    this.#handleImageLoading(textContentElement, /** @type {HTMLElement} */ (toggleButton));
  }

  /**
   * Handle the toggle button click
   * @param {Event} event
   */
  #handleToggle(event) {
    event.preventDefault();
    const container = this;
    const textContent = this.refs.textContent || container.querySelector('[data-text-content]');
    const toggleButton = this.refs.toggleButton || container.querySelector('[data-show-more-toggle]');
    const showMoreText = this.refs.showMoreText || container.querySelector('.text-block__show-more-text');
    const showLessText = this.refs.showLessText || container.querySelector('.text-block__show-less-text');

    if (!textContent || !toggleButton) return;

    if (this.#expanded) {
      container.dataset.expanded = 'false';
      toggleButton.setAttribute('aria-expanded', 'false');
      if (showMoreText) /** @type {HTMLElement} */ (showMoreText).style.display = '';
      if (showLessText) /** @type {HTMLElement} */ (showLessText).style.display = 'none';
    } else {
      container.dataset.expanded = 'true';
      toggleButton.setAttribute('aria-expanded', 'true');
      if (showMoreText) /** @type {HTMLElement} */ (showMoreText).style.display = 'none';
      if (showLessText) /** @type {HTMLElement} */ (showLessText).style.display = '';
    }

    const textContentElement = /** @type {HTMLElement} */ (textContent);
    const { startHeight, endHeight } = !this.#expanded ? this.#expand(textContentElement) : this.#collapse(textContentElement);
    this.#animateHeight(textContentElement, startHeight, endHeight);
    
    this.#expanded = !this.#expanded;
  }

  /**
   * Handles expanding the content
   * @param {HTMLElement} textContent
   * @returns {{startHeight: number, endHeight: number}}
   */
  #expand(textContent) {
    const startHeight = this.#collapsedHeight || textContent.offsetHeight;
    
    const originalDisplay = getComputedStyle(textContent).display;
    const originalWebkitLineClamp = textContent.style.webkitLineClamp;
    const originalOverflow = textContent.style.overflow;
    
    textContent.style.display = 'block';
    textContent.style.webkitLineClamp = 'unset';
    textContent.style.overflow = 'visible';
    textContent.style.height = 'auto';
    
    void textContent.offsetHeight;
    
    const endHeight = textContent.scrollHeight;
    
    textContent.style.display = originalDisplay;
    textContent.style.webkitLineClamp = originalWebkitLineClamp;
    textContent.style.overflow = originalOverflow;
    textContent.style.height = `${startHeight}px`;

    return { startHeight, endHeight };
  }

  /**
   * Handles collapsing the content
   * @param {HTMLElement} textContent
   * @returns {{startHeight: number, endHeight: number}}
   */
  #collapse(textContent) {
    const startHeight = textContent.offsetHeight;
    const endHeight = this.#collapsedHeight;

    return { startHeight, endHeight };
  }

  /**
   * Initializes a height transition animation
   * @param {HTMLElement} textContent
   * @param {number} startHeight
   * @param {number} endHeight
   */
  #animateHeight(textContent, startHeight, endHeight) {
    // Cancel any ongoing animation
    this.#animation?.cancel();

    if (!this.#expanded) {
      textContent.style.display = 'block';
      textContent.style.webkitLineClamp = 'unset';
    }

    textContent.style.overflow = 'hidden';
    textContent.style.height = `${startHeight}px`;
    
    void textContent.offsetHeight;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#animation = textContent.animate(
          {
            height: [`${startHeight}px`, `${endHeight}px`],
          },
          {
            duration: this.#animationSpeed,
            easing: 'ease-in-out',
          }
        );

        this.#animation.onfinish = () => this.#onAnimationFinish(textContent);
      });
    });
  }

  /**
   * Handles the animation finish event
   * @param {HTMLElement} textContent
   */
  #onAnimationFinish(textContent) {
    if (this.#expanded) {
      textContent.style.removeProperty('height');
      textContent.style.removeProperty('overflow');
      textContent.style.display = 'block';
      textContent.style.webkitLineClamp = 'unset';
    } else {
      textContent.style.removeProperty('height');
      textContent.style.removeProperty('overflow');
      textContent.style.display = '-webkit-box';
      textContent.style.webkitLineClamp = String(this.#truncateLines);
      this.#collapsedHeight = textContent.offsetHeight;
    }
  }

  /**
   * @param {HTMLElement} textContent
   * @param {HTMLElement} toggleButton
   */
  #checkIfTruncationNeeded(textContent, toggleButton) {
    const lineHeight = parseFloat(getComputedStyle(textContent).lineHeight);
    const maxHeight = lineHeight * this.#truncateLines;
    const actualHeight = textContent.scrollHeight;

    if (actualHeight <= maxHeight) {
      /** @type {HTMLElement} */ (toggleButton).style.display = 'none';
    } else {
      /** @type {HTMLElement} */ (toggleButton).style.display = '';
    }
  }

  /**
   * @param {HTMLElement} textContent
   * @param {HTMLElement} toggleButton
   */
  #handleImageLoading(textContent, toggleButton) {
    const images = textContent.querySelectorAll('img');
    if (images.length === 0) return;

    let imagesLoaded = 0;
    const checkComplete = () => {
      imagesLoaded++;
      if (imagesLoaded === images.length) {
        this.#checkIfTruncationNeeded(textContent, toggleButton);
      }
    };

    images.forEach((/** @type {HTMLImageElement} */ img) => {
      if (img.complete) {
        checkComplete();
      } else {
        img.addEventListener('load', checkComplete, { once: true });
        img.addEventListener('error', checkComplete, { once: true });
      }
    });

    if (images.length === imagesLoaded) {
      this.#checkIfTruncationNeeded(textContent, toggleButton);
    }
  }
}

if (!customElements.get('text-show-more')) {
  customElements.define('text-show-more', TextShowMore);
}

export default TextShowMore;
